// Turning an annotation query's rows into positioned marks on a time series.
//
// **Where the rows come from.** An `AnnotationSpec` on a time-series display is
// a SECOND built query over a different studio source — the marks live in an
// events view, the series in a metrics view. The backend compiles that query
// itself (`studio_query.annotation_charts`) and fans it out with the tile it
// belongs to: `POST /mine/dashboards/{id}/query` returns the mark set as a
// second entry in `charts`, riding immediately after its chart, and
// `POST /mine/preview` returns it under `annotation`. So the marks arrive as an
// ordinary `ChartResult` on the ordinary settle path, with the same
// `result_key`, the same cache and the same budget charge as everything else.
// Do not build that query or recompute its uuid5 id on the client.
//
// What this module does is WHERE on the drawn axis each event goes. That has to
// be arithmetic over the bucket labels the series was actually drawn from,
// because the axis is CATEGORICAL — one slot per bucket — so a mark's x is
// where its timestamp falls between two buckets, not where it falls on a
// continuous time scale the chart does not have.
import type { AnnotationSpec, ChartResult, ChartSpec, TimeSeriesDisplay } from "@bi/shared/types";

/** The annotation spec on a chart, or `null` for a chart that declares none. */
export function annotationsOf(chart: ChartSpec): AnnotationSpec | null {
  const display = chart.display as TimeSeriesDisplay | undefined;
  if (!display || !("annotations" in display)) return null;
  return display.annotations ?? null;
}

export interface AnnotationMark {
  /** The raw timestamp as the warehouse returned it. */
  at: string;
  label: string;
  /** The `kind_column` value — `deploy`, `lifecycle`, whatever the events
   *  source uses. Not interpreted here: it picks a colour slot and nothing
   *  else, so a kind this app has never seen still draws. */
  kind: string;
  /** Where the mark sits across the plot, 0 at the first bucket and 1 at the
   *  last. */
  position: number;
}

export interface AnnotationMarkSet {
  marks: AnnotationMark[];
  /** Marks whose timestamp fell outside the plotted buckets. DROPPED rather
   *  than clamped to an edge: a mark pinned to the first bucket because it
   *  happened before it reads as an event that happened THEN, which is a
   *  wrong number wearing a label. Counted so the card can say so. */
  outsideCount: number;
}

/** A bucket or event label as milliseconds, read as UTC when it carries no zone.
 *
 *  The zone half is the whole reason this is a function rather than a call to
 *  `Date.parse`. A bare date (`2026-09-03`) is UTC midnight per the spec, but a
 *  bare DATE**TIME** (`2026-09-03 00:00:00`, which is how BigQuery renders one)
 *  is parsed as LOCAL time by every engine — so a mark and the bucket it belongs
 *  in were being measured on two different clocks, and the mark landed hours off
 *  for anybody not sitting in UTC. Both sides go through here, so both are read
 *  the same way. */
function bucketTime(label: string): number {
  const text = label.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return Date.parse(`${text}T00:00:00Z`);
  const iso = text.replace(" ", "T");
  const zoned = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`;
  const parsed = Date.parse(zoned);
  return Number.isNaN(parsed) ? Date.parse(text) : parsed;
}

/** How the chart under the marks lays its buckets out.
 *
 *  `point` is a line chart: bucket `i` of `n` sits at `i / (n - 1)`, so the
 *  first bucket is the left edge and the last is the right edge. `step` is a
 *  bar chart: each bucket owns a slice of width `1 / n` and its bar is CENTRED
 *  in it, so bucket `i` sits at `(i + 0.5) / n`. Drawing a deploy mark with the
 *  line mapping over a bar chart puts it half a bar off, which on a
 *  seven-bucket chart is most of a bar — the mark then reads as belonging to
 *  the wrong day, which is worse than not drawing it. */
export type MarkAxisMode = "point" | "step";

/**
 * Place each annotation row on the categorical axis `buckets` describes.
 *
 * `result` is the annotation query's own `ChartResult`; its columns are the
 * three the annotation names, and they are looked up BY NAME rather than by
 * position, because a raw query's column order is the compiler's business and
 * reading it positionally is how a label ends up where a timestamp belongs.
 */
export function annotationMarks(
  spec: AnnotationSpec,
  result: ChartResult,
  buckets: readonly string[],
  mode: MarkAxisMode = "point",
): AnnotationMarkSet {
  if (buckets.length === 0) return { marks: [], outsideCount: 0 };
  const atIndex = result.columns.indexOf(spec.at_column);
  const labelIndex = result.columns.indexOf(spec.label_column);
  const kindIndex = result.columns.indexOf(spec.kind_column);
  if (atIndex < 0) return { marks: [], outsideCount: 0 };

  const times = buckets.map((b) => bucketTime(b));
  if (times.some((t) => Number.isNaN(t))) return { marks: [], outsideCount: 0 };
  const first = times[0] as number;
  const last = times[times.length - 1] as number;

  const marks: AnnotationMark[] = [];
  let outsideCount = 0;
  for (const row of result.rows) {
    const rawAt = row[atIndex];
    if (typeof rawAt !== "string" && typeof rawAt !== "number") continue;
    const at = String(rawAt);
    const t = bucketTime(at);
    if (Number.isNaN(t)) continue;
    if (t < first || t > last) {
      outsideCount += 1;
      continue;
    }
    marks.push({
      at,
      label: labelIndex >= 0 ? String(row[labelIndex] ?? "") : "",
      kind: kindIndex >= 0 ? String(row[kindIndex] ?? "") : "",
      position: positionOf(t, times, mode),
    });
  }
  return { marks, outsideCount };
}

/** Where `t` sits across the bucket slots, 0..1. A single-bucket chart puts
 *  everything in the middle of its one slot, which is the only place a mark can
 *  honestly go when the axis has no extent. */
function positionOf(t: number, times: readonly number[], mode: MarkAxisMode): number {
  if (times.length === 1) return 0.5;
  const lastIndex = times.length - 1;
  const scale = (slot: number): number =>
    mode === "step" ? (slot + 0.5) / times.length : slot / lastIndex;
  for (let i = 0; i < lastIndex; i += 1) {
    const a = times[i] as number;
    const b = times[i + 1] as number;
    if (t >= a && t <= b) {
      const span = b - a;
      const frac = span === 0 ? 0 : (t - a) / span;
      return scale(i + frac);
    }
  }
  return scale(lastIndex);
}

/** Marks that would overlap once drawn, folded so their labels stay readable.
 *
 *  Two deploys an hour apart on a ninety-day chart are two lines a pixel apart
 *  with two labels on top of each other. Folding them into one mark that says
 *  how many there are keeps the information (something happened here, more than
 *  once) and drops only the part that was unreadable anyway. */
export function foldNearbyMarks(
  marks: readonly AnnotationMark[],
  minGap = 0.02,
): { mark: AnnotationMark; count: number }[] {
  const out: { mark: AnnotationMark; count: number }[] = [];
  for (const mark of [...marks].sort((a, b) => a.position - b.position)) {
    const previous = out[out.length - 1];
    if (previous && mark.position - previous.mark.position < minGap) {
      previous.count += 1;
      continue;
    }
    out.push({ mark, count: 1 });
  }
  return out;
}

/**
 * Which mark set belongs to which chart, read off a `/query` response.
 *
 * The backend's contract (`query_studio_dashboard`'s docstring): a requested
 * chart's entry is followed IMMEDIATELY by its annotation entry, whose synthetic
 * id is not one the client asked for. So the mapping comes from the response
 * alone. Never recompute the backend's uuid5 to guess the id.
 *
 * Defensive about ordering: an unrequested entry with no requested entry before
 * it is ignored (it belongs to nothing), and only the FIRST unrequested entry
 * after a chart is taken as that chart's, so a future second trailing entry
 * cannot silently reassign the marks.
 */
export function annotationIdsByChart(
  requested: readonly string[],
  entries: readonly { chartId: string }[],
): Record<string, string> {
  const asked = new Set(requested);
  const map: Record<string, string> = {};
  let owner: string | null = null;
  for (const entry of entries) {
    if (asked.has(entry.chartId)) {
      owner = entry.chartId;
      continue;
    }
    if (owner !== null && map[owner] === undefined) map[owner] = entry.chartId;
  }
  return map;
}
